import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BadgeOriginal } from 'src/components/BadgeOriginal';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { LockedScoreButtons } from 'src/components/LockedScoreButtons';
import { LockedScorePopper } from 'src/components/LockedScorePopper';
import { ScoreView } from 'src/components/ScoreView';
import { ScoreWithDetailsLink } from 'src/components/ScoreWithDetailsLink';
import TooltipExclamation from 'src/components/TooltipExclamation';
import { VariationEditableContent } from 'src/components/VariationEditableContent';
import { VariationImageContent } from 'src/components/VariationImageContent';
import { ASSET_TYPE } from 'src/data/asset';
import { BLOCKED_REASON, isLockedScoreBlockedReason } from 'src/data/common';
import { isNonEnglish, LANGUAGE } from 'src/data/generation';
import { isImageGenerationTool } from 'src/data/generationTool';
import { isDocumentProjectType, PROJECT_TYPE } from 'src/data/project';
import { CSS_CLASS } from 'src/data/ui';
import { useReferenceElement } from 'src/lib/hooks';
import { CHARACTER_BULLET } from 'src/lib/string';

import { Counters } from './components/Counters';
import { useStyles } from './useStyles';

export const Variation = forwardRef(
  (
    {
      className,
      classes,
      variationId,
      projectType,
      assetType,
      description,
      textContent,
      content,
      markup,
      score,
      counter,
      onChange,
      selected,
      onCloseError,
      error,
      leftActions,
      actions,
      forceDisplayActions,
      displayScore,
      readOnly,
      updating,
      detailsAllowed,
      onClick,
      forceClickAllow,
      blockedReason,
      onGetScoreClick,
      onLockedUpgradeClick,
      onUndoClick,
      undoDisabled,
      hideUndo,
      disabled,
      setEditor,
      hideMarks,
      language,
      onChangeClassifiedGenerationTool,
      generationTool,
      isLoadingClassifiedGenerationTool,
      scoringMethod,
      supportSize,
      forceHideErrorExclamation,
      hideActionsOnError,
      onCopy,
      onCut,
      onInsightsClick,
      isDraft,
      highlighted,
      imagePath,
      imageBlob,
      imageOverlayData,
      displayInsights,
      statusLabel,
      lastUpdateTime,
      isRecomposeOriginal,
      multiPromptTool,
      ...restProps
    },
    ref
  ) => {
    const [transition, setTransition] = useState(false);
    const refVariation = useRef();

    useEffect(() => {
      if (highlighted) {
        setTransition(true);
      } else {
        setTimeout(() => {
          setTransition(false);
        }, 4000);
      }
    }, [highlighted]);

    const styles = useStyles({ classes, transition });

    const refActions = useRef();

    const counterValue = counter?.charsCount || 0;
    const counterStrict = counter?.strict || false;
    const hasActions = Boolean(actions);
    const actionsVisible = forceDisplayActions || (Boolean(error) && !hideActionsOnError);
    const isEmpty = counterValue === 0;

    const [editorFocused, setEditorFocused] = useState(false);
    const handleEditorFocusChange = useCallback((focused) => {
      setEditorFocused(focused);
    }, []);

    const handleErrorTooltipClose = useCallback(() => {
      if (onCloseError) {
        onCloseError();
      }
    }, [onCloseError]);

    const { referenceElement, setReferenceElement, clearReferenceElement } = useReferenceElement();

    const handleGetScoreClick = useCallback(
      (element) => {
        // Display internal popover for all blocked reasons except "performance predictions limit" - for this one we display "upgrade" popup in outside handler
        if (blockedReason !== BLOCKED_REASON.performancePredictionsLimit) {
          setReferenceElement(element);
        }

        if (onGetScoreClick) {
          onGetScoreClick();
        }
      },
      [blockedReason, onGetScoreClick, setReferenceElement]
    );

    const isLimitedScoring = isLockedScoreBlockedReason(blockedReason);

    const displayActions = hasActions && (selected || actionsVisible);
    const isEditorProject = isDocumentProjectType(projectType);
    const isImageTool = isImageGenerationTool(generationTool);

    const generationToolIcon = generationTool && (
      <GenerationToolIcon
        className={styles.toolBadge}
        tool={generationTool}
      />
    );

    const counterValid = counterValue >= 0 && counterValue <= (counter?.maxChars || 0);
    const hasStatusLabel = statusLabel != null;

    return (
      <div
        className={cx(styles.root, className, CSS_CLASS.scoreHoverableParent, {
          [styles.editing]: editorFocused,
          [styles.empty]: isEmpty,
          [styles.selected]: selected,
          [styles.displayEditorActions]: isEditorProject && displayActions,
          [styles.displayActions]: displayActions,
          [styles.displayScore]: displayScore,
          [styles.detailsAllowed]: detailsAllowed,
          [styles.error]: error,
          [styles.extraHeight]: isLimitedScoring && !hasStatusLabel,
          [styles.withStatusHeight]: hasStatusLabel && !isLimitedScoring,
          [styles.withStatusHeightLocked]: hasStatusLabel && isLimitedScoring,
          [styles.isDraft]: isDraft,
          [styles.highlighted]: highlighted || isRecomposeOriginal,
          [styles.isImageTool]: isImageTool,
          [styles.counterInvalid]: counterStrict && !counterValid,
        })}
        ref={ref}
        onClick={onClick}
        data-testid={`${generationTool}_${variationId}_VariationDraft`}
        {...restProps}
      >
        {leftActions && <div className={styles.leftActions}>{leftActions}</div>}

        <div
          className={styles.variationContent}
          ref={refVariation}
        >
          {!isImageTool && (
            <div className={styles.descriptionWrapper}>
              {description ? (
                <CustomTypography
                  className={styles.description}
                  variant={TYPOGRAPHY_VARIANT.regular12}
                >
                  {description}
                </CustomTypography>
              ) : null}
              {description && isRecomposeOriginal && (
                <span className={styles.bullet}>{CHARACTER_BULLET}</span>
              )}
              {isRecomposeOriginal && <BadgeOriginal className={styles.originalTag} />}
            </div>
          )}

          {isImageTool ? (
            <VariationImageContent
              className={styles.imageVariationContent}
              generationTool={generationTool}
              title={description || 'Image'}
              description={textContent}
              imageUrl={imagePath}
              imageBlob={imageBlob}
              score={score}
              overlayData={imageOverlayData}
              updating={updating}
              isRecomposeOriginal={isRecomposeOriginal}
              multiPromptTool={multiPromptTool}
            />
          ) : (
            <div
              className={styles.editorWrapper}
              data-testid="variationDraftText"
            >
              <VariationEditableContent
                className={cx(styles.editor, { [styles.editable]: !disabled })}
                content={content}
                onChange={onChange}
                onFocusChange={handleEditorFocusChange}
                onCopy={onCopy}
                onCut={onCut}
                setEditor={setEditor}
                disabled={disabled}
                hideMarks={editorFocused}
              />
            </div>
          )}
        </div>

        {!isImageTool && (
          <Counters
            className={styles.textCounter}
            variationRootElement={refVariation.current}
            assetType={assetType}
            counter={counter}
            language={language}
          />
        )}

        <div
          className={cx(styles.actions, { [styles.fullWidthActions]: isEditorProject })}
          ref={refActions}
        >
          {actions}
        </div>

        {displayScore &&
          !isImageTool /* score is handled by `ImageVariationContent` */ &&
          (isLimitedScoring ? (
            <LockedScoreButtons
              className={styles.lockedButtons}
              onGetScoreClick={handleGetScoreClick}
              onUndoClick={onUndoClick}
              disabled={undoDisabled}
              hideUndo={hideUndo || blockedReason === BLOCKED_REASON.performancePredictionsLimit}
              generationToolIcon={generationToolIcon}
              vertical
            />
          ) : detailsAllowed ? (
            <ScoreWithDetailsLink
              className={styles.score}
              assetType={assetType}
              score={counterStrict && !counterValid ? 0 : score}
              loading={updating}
              isLoadingClassifiedGenerationTool={isLoadingClassifiedGenerationTool}
              expanded={selected || forceDisplayActions}
              blockedReason={blockedReason}
              locked={!!blockedReason}
              displayInsights={displayInsights}
              generationTool={generationTool}
              scoringMethod={scoringMethod}
              supportSize={supportSize}
              isNonEnglishLanguage={isNonEnglish(language)}
              enableSectionScoreTooltip
              projectType={projectType}
              onScoreTooltipSelectTool={onChangeClassifiedGenerationTool}
              onClick={
                (!isLimitedScoring && displayInsights) || forceClickAllow ? onInsightsClick : null
              }
              tooltipOverride={
                counterStrict && !counterValid
                  ? {
                      title: '',
                      text: 'This text exceeds\nthe character limit\nfor this asset type.',
                    }
                  : null
              }
            />
          ) : (
            <ScoreView
              className={styles.score}
              score={score}
              updating={updating}
              blockedReason={blockedReason}
              locked={!!blockedReason}
              generationTool={generationTool}
            />
          ))}

        {hasStatusLabel && statusLabel}

        {!forceHideErrorExclamation && (
          <TooltipExclamation
            classes={{ root: styles.errorTooltip }}
            open={Boolean(error)}
            anchor={refActions.current}
            onClose={handleErrorTooltipClose}
            content={error}
            icon="error"
            placement="top"
          />
        )}

        <LockedScorePopper
          referenceElement={referenceElement}
          onUpgrade={onLockedUpgradeClick}
          onClose={clearReferenceElement}
        />
      </div>
    );
  }
);

Variation.propTypes = {
  variationId: PropTypes.string.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape(),
  projectType: PropTypes.oneOf(Object.values(PROJECT_TYPE)),
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)),
  description: PropTypes.string,
  textContent: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  markup: PropTypes.arrayOf(PropTypes.shape()),
  score: PropTypes.number,
  counter: PropTypes.shape({
    charsCount: PropTypes.number,
    wordsCount: PropTypes.number,
    maxChars: PropTypes.number,
    maxWords: PropTypes.number,
    hint: PropTypes.string,
    strict: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  onCloseError: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  leftActions: PropTypes.node,
  actions: PropTypes.node,
  forceDisplayActions: PropTypes.bool,
  displayScore: PropTypes.bool,
  readOnly: PropTypes.bool,
  updating: PropTypes.bool,
  detailsAllowed: PropTypes.bool,
  onClick: PropTypes.func,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
  onGetScoreClick: PropTypes.func,
  onLockedUpgradeClick: PropTypes.func,
  onUndoClick: PropTypes.func,
  undoDisabled: PropTypes.bool,
  hideUndo: PropTypes.bool,
  disabled: PropTypes.bool,
  setEditor: PropTypes.func,
  hideMarks: PropTypes.bool,
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
  onChangeClassifiedGenerationTool: PropTypes.func,
  generationTool: PropTypes.string,
  isLoadingClassifiedGenerationTool: PropTypes.bool,
  supportSize: PropTypes.number,
  scoringMethod: PropTypes.shape({}),
  forceClickAllow: PropTypes.bool,
  forceHideErrorExclamation: PropTypes.bool,
  hideActionsOnError: PropTypes.bool,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
  onInsightsClick: PropTypes.func,
  isDraft: PropTypes.bool,
  highlighted: PropTypes.bool,
  imagePath: PropTypes.string,
  imageBlob: PropTypes.string,
  imageOverlayData: PropTypes.shape({}),
  displayInsights: PropTypes.bool,
  statusLabel: PropTypes.node,
  lastUpdateTime: PropTypes.string,
  isRecomposeOriginal: PropTypes.bool,
  multiPromptTool: PropTypes.string,
};

Variation.defaultProps = {
  className: null,
  classes: null,
  projectType: null,
  assetType: null,
  description: null,
  markup: null,
  score: 0,
  counter: null,
  selected: false,
  onCloseError: null,
  error: null,
  leftActions: null,
  actions: null,
  forceDisplayActions: false,
  displayScore: false,
  readOnly: false,
  updating: false,
  detailsAllowed: false,
  onClick: null,
  blockedReason: null,
  onGetScoreClick: null,
  onLockedUpgradeClick: null,
  onUndoClick: null,
  undoDisabled: false,
  hideUndo: false,
  disabled: false,
  setEditor: null,
  hideMarks: false,
  language: null,
  onChangeClassifiedGenerationTool: null,
  generationTool: null,
  isLoadingClassifiedGenerationTool: false,
  supportSize: null,
  scoringMethod: null,
  forceClickAllow: false,
  forceHideErrorExclamation: false,
  hideActionsOnError: false,
  onCopy: null,
  onCut: null,
  onInsightsClick: null,
  isDraft: false,
  highlighted: false,
  imagePath: null,
  imageBlob: null,
  imageOverlayData: null,
  displayInsights: false,
  statusLabel: null,
  lastUpdateTime: null,
  isRecomposeOriginal: false,
  multiPromptTool: null,
};
